'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowUpDown,
  Eye,
  Filter,
  LayoutGrid,
  Map,
  MapPin,
  Tag,
  Users,
} from 'lucide-react'
import { CountryFlag } from '@/lib/countryFlags'
import { getMultiLocationFilterDisplay } from '@/lib/locationRegions'
import type { AccordionRegion } from '@/lib/locationRegions'
import { DiscoverFilterSheet, type DiscoverFilterDraft } from './DiscoverFilterSheet'
import {
  filterPillClassName,
  filterPrimaryButtonClassName,
} from '@/components/shared/filterSheetStyles'

interface DiscoverFilterBarProps {
  appliedFilters: DiscoverFilterDraft
  onApplyFilters: (draft: DiscoverFilterDraft) => void
  typeLabel: string
  sortLabel: string
  locationLabel: string
  locationEffectivelyAll: boolean
  locationFlagCountry?: string | null
  showFypOption?: boolean
  showFollowingOption?: boolean
  canChangeTattooSorting?: boolean
}

function FilterDisplayPill({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button type="button" className={filterPillClassName} onClick={onClick}>
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  )
}

export function DiscoverFilterBar({
  appliedFilters,
  onApplyFilters,
  typeLabel,
  sortLabel,
  locationLabel,
  locationEffectivelyAll,
  locationFlagCountry,
  showFypOption = true,
  showFollowingOption = false,
  canChangeTattooSorting = true,
}: DiscoverFilterBarProps) {
  const { t } = useTranslation()
  const [filterSheetOpen, setFilterSheetOpen] = React.useState(false)
  const { discoverType } = appliedFilters

  const openFilterSheet = () => setFilterSheetOpen(true)

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={filterPrimaryButtonClassName}
          aria-label={t('discover.filters.open', 'Open filters')}
          onClick={openFilterSheet}
        >
          <Filter className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto hide-scrollbar">
          <FilterDisplayPill
            icon={<LayoutGrid className="h-4 w-4" />}
            label={typeLabel}
            onClick={openFilterSheet}
          />
          <FilterDisplayPill
            icon={
              locationEffectivelyAll || !locationFlagCountry ? (
                <MapPin className="h-4 w-4" />
              ) : (
                <CountryFlag
                  country={locationFlagCountry}
                  size="sm"
                  showBorder={false}
                  className="h-4 w-4 shrink-0 !rounded-sm"
                />
              )
            }
            label={locationLabel}
            onClick={openFilterSheet}
          />
          <FilterDisplayPill
            icon={<ArrowUpDown className="h-4 w-4" />}
            label={sortLabel}
            onClick={openFilterSheet}
          />
          {discoverType === 'flashes' && appliedFilters.showDeals ? (
            <FilterDisplayPill
              icon={<Tag className="h-4 w-4" />}
              label={t('discover.filters.dealsOn', 'Deals only')}
              onClick={openFilterSheet}
            />
          ) : null}
          {discoverType === 'flashes' && appliedFilters.includeBooked ? (
            <FilterDisplayPill
              icon={<Eye className="h-4 w-4" />}
              label={t('discover.filters.showSold', 'Show sold')}
              onClick={openFilterSheet}
            />
          ) : null}
          {discoverType === 'artists' && appliedFilters.activeTab === 'map' ? (
            <FilterDisplayPill
              icon={<Map className="h-4 w-4" />}
              label={t('discover.filters.mapView', 'Map')}
              onClick={openFilterSheet}
            />
          ) : null}
          {discoverType === 'artists' && appliedFilters.showFollowingOnly ? (
            <FilterDisplayPill
              icon={<Users className="h-4 w-4" />}
              label={t('discover.filters.followingOnly', 'Following only')}
              onClick={openFilterSheet}
            />
          ) : null}
        </div>
      </div>

      <DiscoverFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        appliedFilters={appliedFilters}
        onApply={onApplyFilters}
        showFypOption={showFypOption}
        showFollowingOption={showFollowingOption}
        canChangeTattooSorting={canChangeTattooSorting}
      />
    </>
  )
}

export function buildDiscoverLocationLabel(
  selectedLocations: string[],
  allLocationsText: string,
  locationAccordionRegions: AccordionRegion[],
  locationEffectivelyAll: boolean,
): string {
  if (locationEffectivelyAll) {
    return allLocationsText
  }
  return getMultiLocationFilterDisplay(selectedLocations, allLocationsText, locationAccordionRegions)
}
